import api from './api';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ControlType {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  periodDays?: number | null;
  maxHours?: number | null;
  /** Max concurrent sessions for the whole group (shared pool). Default seed 20. */
  maxConcurrent?: number | null;
  extensionsAllowed?: boolean;
  exhaustionMode?: string;
  toolSeatJson?: string;
}

export interface LicenseGroupType {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  toolsJson?: string;
}

export interface WorkspacePackage {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  licenseGroupTypeId?: number | null;
  controlTypeId?: number | null;
  toolsJson?: string;
  licenseCountsJson?: string;
  /** IWM catalog profile id — picked from WorkspaceService.getProfiles(), not free text. */
  iwmProfileId?: string | null;
  /** IWM CreateSessionBody memory fields, e.g. "4Gi" — no CPU field in the frozen contract. */
  memoryRequest?: string | null;
  memoryLimit?: string | null;
}

export interface IwmProfile {
  id: string;
  displayName?: string;
  [key: string]: unknown;
}

/** IWM list endpoints wrap rows in `{ <key>: [...] }`, not a bare array. */
export function unwrapIwmList<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) {
    return (data as Record<string, T[]>)[key];
  }
  return [];
}

/** IWM GET /profiles returns `{ profiles: IwmProfile[] }`. */
export function unwrapIwmProfiles(data: unknown): IwmProfile[] {
  return unwrapIwmList<IwmProfile>(data, 'profiles');
}

/** IWM GET /sessions returns `{ sessions: WorkspaceSession[] }`. */
export function unwrapIwmSessions(data: unknown): WorkspaceSession[] {
  return unwrapIwmList<WorkspaceSession>(data, 'sessions');
}

export interface GroupType {
  id: number;
  code: string;
  kind: string;
  displayName: string;
  description?: string;
}

export type NfsRole = 'tools' | 'labs' | 'pdk' | 'home';

export interface NfsLocation {
  id: number;
  code: string;
  role: NfsRole;
  nfsServer: string;
  nfsPath: string;
  mountPath: string;
  readOnly: boolean;
}

export interface GroupMember {
  id?: number;
  userId: string;
  email?: string;
  isPrimary?: boolean;
}

export interface GroupInstance {
  id: number;
  name?: string;
  code?: string;
  startDate?: string | null;
  endDate?: string | null;
  packageId?: number | null;
  controlTypeId?: number | null;
  toolsNfsLocationId?: number | null;
  labsNfsLocationId?: number | null;
  pdkNfsLocationId?: number | null;
  homeNfsLocationId?: number | null;
  affinityServerIds?: string;
  affinityServerIdsJson?: string;
  scheduledHours?: number | null;
  scheduledHoursJson?: string;
  maxWeekHours?: number | null;
  ttlMinutes?: number | null;
  profileId?: string;
  groupTypeId?: number;
  groupType?: GroupType;
  extensionPossible?: boolean;
  status?: string;
  members?: GroupMember[];
  /** Populated by list endpoint for tracking */
  memberCount?: number;
}

export interface AttachPreviewRow {
  uniqueUserId: number;
  userId: string;
  email?: string | null;
  programCode?: string | null;
  programKind?: string | null;
  programInstanceId?: string | null;
  suggestedGroupInstanceId?: number | null;
  suggestedGroupName?: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  reasons: string[];
  conflicts: string[];
  existingMemberships: { groupInstanceId: number; groupName: string; isPrimary: boolean }[];
  status: 'ready' | 'already_member' | 'no_suggestion' | 'conflict';
}

export interface AttachPreviewResponse {
  rows: AttachPreviewRow[];
  groups: {
    id: number;
    name: string;
    groupTypeCode?: string | null;
    groupTypeKind?: string | null;
  }[];
  summary: {
    total: number;
    ready: number;
    alreadyMember: number;
    noSuggestion: number;
    conflict: number;
  };
}

export interface WorkspaceSession {
  id: string;
  user: string;
  profileId: string;
  status?: string;
  createdAt?: string;
  novncUrl?: string;
  [key: string]: unknown;
}

// ── Definitions ────────────────────────────────────────────────────────────

const defs = '/api/workspace/definitions';

export const listControlTypes = () =>
  api.get<ControlType[]>(`${defs}/control-types`).then((r) => r.data);

export const createControlType = (data: Partial<ControlType>) =>
  api.post<ControlType>(`${defs}/control-types`, data).then((r) => r.data);

export const updateControlType = (id: number, data: Partial<ControlType>) =>
  api.put<ControlType>(`${defs}/control-types/${id}`, data).then((r) => r.data);

export const deleteControlType = (id: number) =>
  api.delete(`${defs}/control-types/${id}`);

export const listLicenseGroupTypes = () =>
  api.get<LicenseGroupType[]>(`${defs}/license-group-types`).then((r) => r.data);

export const createLicenseGroupType = (data: Partial<LicenseGroupType>) =>
  api.post<LicenseGroupType>(`${defs}/license-group-types`, data).then((r) => r.data);

export const updateLicenseGroupType = (id: number, data: Partial<LicenseGroupType>) =>
  api.put<LicenseGroupType>(`${defs}/license-group-types/${id}`, data).then((r) => r.data);

export const deleteLicenseGroupType = (id: number) =>
  api.delete(`${defs}/license-group-types/${id}`);

export const listPackages = () =>
  api.get<WorkspacePackage[]>(`${defs}/packages`).then((r) => r.data);

export const createPackage = (data: Partial<WorkspacePackage>) =>
  api.post<WorkspacePackage>(`${defs}/packages`, data).then((r) => r.data);

export const updatePackage = (id: number, data: Partial<WorkspacePackage>) =>
  api.put<WorkspacePackage>(`${defs}/packages/${id}`, data).then((r) => r.data);

export const deletePackage = (id: number) =>
  api.delete(`${defs}/packages/${id}`);

/** Live IWM profile catalog (for the Package "IWM Profile" picker) — not free text. */
export const listIwmProfiles = () =>
  api.get('/api/workspace/iwm/profiles').then((r) => unwrapIwmProfiles(r.data));

export const listGroupTypes = () =>
  api.get<GroupType[]>(`${defs}/group-types`).then((r) => r.data);

export const createGroupType = (data: Partial<GroupType>) =>
  api.post<GroupType>(`${defs}/group-types`, data).then((r) => r.data);

export const updateGroupType = (id: number, data: Partial<GroupType>) =>
  api.put<GroupType>(`${defs}/group-types/${id}`, data).then((r) => r.data);

export const deleteGroupType = (id: number) =>
  api.delete(`${defs}/group-types/${id}`);

export const seedDefinitions = () =>
  api.post(`${defs}/seed`).then((r) => r.data);

// ── NFS locations ──────────────────────────────────────────────────────────

export const listNfsLocations = () =>
  api.get<NfsLocation[]>('/api/workspace/nfs-locations').then((r) => r.data);

export const createNfsLocation = (data: Partial<NfsLocation>) =>
  api.post<NfsLocation>('/api/workspace/nfs-locations', data).then((r) => r.data);

export const updateNfsLocation = (id: number, data: Partial<NfsLocation>) =>
  api.put<NfsLocation>(`/api/workspace/nfs-locations/${id}`, data).then((r) => r.data);

export const deleteNfsLocation = (id: number) =>
  api.delete(`/api/workspace/nfs-locations/${id}`);

// ── Group instances ────────────────────────────────────────────────────────

export const listGroupInstances = () =>
  api.get<GroupInstance[]>('/api/workspace/groups').then((r) => r.data);

export const getGroupInstance = (id: number) =>
  api.get<GroupInstance>(`/api/workspace/groups/${id}`).then((r) => r.data);

export const createGroupInstance = (data: Partial<GroupInstance>) =>
  api.post<GroupInstance>('/api/workspace/groups', data).then((r) => r.data);

export const updateGroupInstance = (id: number, data: Partial<GroupInstance>) =>
  api.put<GroupInstance>(`/api/workspace/groups/${id}`, data).then((r) => r.data);

export const deleteGroupInstance = (id: number) =>
  api.delete(`/api/workspace/groups/${id}`);

export const addGroupMember = (groupId: number, data: Partial<GroupMember>) =>
  api
    .post<GroupMember | GroupMember[] | { members: GroupMember[] }>(
      `/api/workspace/groups/${groupId}/members`,
      data,
    )
    .then((r) => r.data);

/** Bulk attach existing Unique Users as group members (idempotent). */
export const addGroupMembers = (groupId: number, members: Partial<GroupMember>[]) =>
  api
    .post<GroupMember | GroupMember[] | { members: GroupMember[] }>(
      `/api/workspace/groups/${groupId}/members`,
      { members },
    )
    .then((r) => r.data);

export const removeGroupMember = (groupId: number, memberId: number) =>
  api.delete(`/api/workspace/groups/${groupId}/members/${memberId}`);

/** Bulk remove members by userId (idempotent). */
export const removeGroupMembersByUserIds = (groupId: number, userIds: string[]) =>
  api
    .post<{ ok: boolean; removed: number }>(`/api/workspace/groups/${groupId}/members/remove`, {
      userIds,
    })
    .then((r) => r.data);

export const resolveGroupPolicy = (id: number) =>
  api.post(`/api/workspace/groups/${id}/resolve-policy`).then((r) => r.data);

/** Preview attaching existing Unique Users to a Batch/Group (#31). */
export const previewAttachExistingUsers = (body: {
  uniqueUserIds?: number[];
  userIds?: string[];
  groupInstanceId?: number;
}) =>
  api.post<AttachPreviewResponse>('/api/workspace/attach/preview', body).then((r) => r.data);

// ── Sessions (IWM) ─────────────────────────────────────────────────────────

export const listWorkspaceSessions = (user?: string) =>
  api
    .get<unknown>('/api/workspace/sessions', { params: user ? { user } : undefined })
    .then((r) => unwrapIwmSessions(r.data));

export const createWorkspaceSession = (data: {
  user: string;
  profileId?: string;
  groupInstanceId?: number;
}) => api.post<WorkspaceSession>('/api/workspace/sessions', data).then((r) => r.data);

export const deleteWorkspaceSession = (id: string) =>
  api.delete(`/api/workspace/sessions/${id}`);

/** IC-039 — resolve a person's group entitlements and start their IWM session. */
export const startWorkspaceSession = (userId: string) =>
  api.post<WorkspaceSession>('/api/workspace/sessions/start', { userId }).then((r) => r.data);

export function workspaceSessionDesktopPath(sessionId: string): string {
  return `/api/workspace/sessions/${encodeURIComponent(sessionId)}/desktop`;
}

export function isWorkspaceSessionRunning(status?: string): boolean {
  return String(status || '').toLowerCase() === 'running';
}

/** Open IWM session noVNC via IC in-cluster proxy (not IWM novncUrl). */
export async function openWorkspaceSessionDesktop(sessionId: string): Promise<void> {
  const path = workspaceSessionDesktopPath(sessionId);
  try {
    const r = await api.get<{ desktopUrl?: string }>(path);
    const url = r.data?.desktopUrl;
    if (url && !/^port-forward:/i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number; data?: { message?: string } } })?.response?.status;
    const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
    // 401: new-tab navigation with JWT query so the desk cookie can be set.
    // Any other status (409 not running, 403, 502) must not open a JSON error tab.
    if (status && status !== 401) {
      throw new Error(msg || 'Failed to open desktop');
    }
  }
  const token = localStorage.getItem('authToken') || '';
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  window.open(`${path}${q}`, '_blank', 'noopener,noreferrer');
}
